import { Box, Typography } from '@mui/material';
import { FieldProps } from '@rjsf/utils';
import { colors } from '@src/styles';

const monoFontFamily = "'JetBrains Mono', monospace";

type TRuleValue = string | number | boolean;
type TValueType = 'string' | 'number' | 'boolean';

type TRule = Record<string, TRuleValue | undefined> & {
  operator?: string;
  value?: TRuleValue;
};

const DEFAULT_IDENTIFIER_KEY = 'fieldIdentifier';

const OPERATOR_LABELS: Record<string, string> = {
  equals: 'é igual a',
  not_equals: 'é diferente de',
  has_size_equals_to: 'tem tamanho igual a',
  greater_than: 'é maior que',
  less_than: 'é menor que',
  equals_or_greater_than: 'é maior ou igual a',
  equals_or_less_than: 'é menor ou igual a',
  includes: 'contém',
  not_includes: 'não contém',
  present: 'está preenchido',
  blank: 'está vazio',
};

const VALUELESS_OPERATORS = new Set(['present', 'blank']);

const RULE_COLUMNS = 'minmax(0, 1.1fr) minmax(0, 1fr) minmax(0, 1.5fr) 30px';
const NARROW_RULE = '@container (max-width: 639px)';

const columnLabelSX = {
  fontFamily: monoFontFamily,
  fontSize: '9.5px',
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: colors.brand.textMuted,
} as const;

const getValueType = (value: TRuleValue | undefined): TValueType => {
  if (typeof value === 'boolean') return 'boolean';
  if (typeof value === 'number') return 'number';
  return 'string';
};

const convertValue = (value: TRuleValue | undefined, type: TValueType): TRuleValue => {
  const text = value === undefined || value === null ? '' : String(value);
  if (type === 'boolean') return text.toLowerCase() === 'true';
  if (type === 'number') {
    const parsed = Number(text);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  return text;
};

export const RuleListField = (props: FieldProps) => {
  const rules: TRule[] = Array.isArray(props.formData) ? props.formData : [];
  const identifierKey =
    (props.uiSchema?.['ui:options'] as { identifierKey?: string } | undefined)?.identifierKey ?? DEFAULT_IDENTIFIER_KEY;
  const itemSchema = (props.schema.items ?? {}) as {
    properties?: Record<string, { description?: string; enum?: string[] }>;
  };
  const operators = itemSchema.properties?.operator?.enum ?? Object.keys(OPERATOR_LABELS);
  const fieldHint = itemSchema.properties?.[identifierKey]?.description;
  const getIdentifier = (rule: TRule): string =>
    typeof rule[identifierKey] === 'string' ? (rule[identifierKey] as string) : '';
  const isDisabled = Boolean(props.disabled || props.readonly);
  const isReadOnly = Boolean(props.readonly);

  const emit = (nextRules: TRule[]) => props.onChange(nextRules, props.fieldPathId.path);

  const updateRule = (index: number, patch: Partial<TRule>) => {
    emit(rules.map((rule, ruleIndex) => (ruleIndex === index ? { ...rule, ...patch } : rule)));
  };

  const handleOperatorChange = (index: number, operator: string) => {
    const patch: Partial<TRule> = VALUELESS_OPERATORS.has(operator) ? { operator, value: '' } : { operator };
    updateRule(index, patch);
  };

  const addRule = () => emit([...rules, { [identifierKey]: '', operator: operators[0], value: '' }]);
  const removeRule = (index: number) => emit(rules.filter((_, ruleIndex) => ruleIndex !== index));

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        width: '100%',
        marginBottom: 1,
        containerType: 'inline-size',
      }}
    >
      {props.schema.title && (
        <Typography
          sx={{
            fontFamily: monoFontFamily,
            fontSize: '10px',
            fontWeight: 500,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: colors.brand.textSecondary,
          }}
        >
          {props.schema.title === 'Expressions' ? 'Regras' : props.schema.title} · {rules.length}
        </Typography>
      )}

      {rules.length > 0 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: RULE_COLUMNS, gap: 1, [NARROW_RULE]: { display: 'none' } }}>
          <Typography sx={columnLabelSX}>Campo</Typography>
          <Typography sx={columnLabelSX}>Condição</Typography>
          <Typography sx={columnLabelSX}>Valor</Typography>
          <span />
        </Box>
      )}

      {rules.map((rule, index) => {
        const operator = rule.operator ?? operators[0];
        const isValueless = VALUELESS_OPERATORS.has(operator);
        const valueType = getValueType(rule.value);
        const isFieldMissing = !getIdentifier(rule).trim();
        const rowNumber = index + 1;

        return (
          <Box
            key={index}
            sx={{
              display: 'grid',
              gridTemplateColumns: RULE_COLUMNS,
              gridTemplateAreas: '"field operator value remove"',
              gap: 1,
              alignItems: 'center',
              [NARROW_RULE]: {
                gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) 30px',
                gridTemplateAreas: '"field operator remove" "value value value"',
                padding: '10px',
                borderRadius: '10px',
                border: `1px solid ${colors.brand.line}`,
              },
            }}
          >
            <input
              type="text"
              aria-label={`Campo da regra ${rowNumber}`}
              placeholder="entidade_campo"
              value={getIdentifier(rule)}
              disabled={isDisabled}
              onChange={(event) => updateRule(index, { [identifierKey]: event.target.value })}
              style={{
                gridArea: 'field',
                minWidth: 0,
                fontFamily: monoFontFamily,
                fontSize: '12.5px',
                borderColor: isFieldMissing ? 'rgba(245, 158, 11, 0.6)' : undefined,
              }}
            />

            <select
              aria-label={`Condição da regra ${rowNumber}`}
              value={operator}
              disabled={isDisabled}
              onChange={(event) => handleOperatorChange(index, event.target.value)}
              style={{ gridArea: 'operator', minWidth: 0 }}
            >
              {operators.map((option) => (
                <option key={option} value={option}>
                  {OPERATOR_LABELS[option] ?? option}
                </option>
              ))}
            </select>

            {isValueless ? (
              <Typography
                sx={{ gridArea: 'value', fontSize: '12px', color: colors.brand.textMuted, paddingLeft: '4px' }}
              >
                sem valor para esta condição
              </Typography>
            ) : (
              <Box sx={{ gridArea: 'value', display: 'flex', gap: 1, minWidth: 0 }}>
                <select
                  aria-label={`Tipo do valor da regra ${rowNumber}`}
                  value={valueType}
                  disabled={isDisabled}
                  onChange={(event) =>
                    updateRule(index, { value: convertValue(rule.value, event.target.value as TValueType) })
                  }
                  style={{ width: 92, flexShrink: 0, fontSize: '12px' }}
                >
                  <option value="string">Texto</option>
                  <option value="number">Número</option>
                  <option value="boolean">Sim/Não</option>
                </select>
                {valueType === 'boolean' ? (
                  <select
                    aria-label={`Valor da regra ${rowNumber}`}
                    value={rule.value ? 'true' : 'false'}
                    disabled={isDisabled}
                    onChange={(event) => updateRule(index, { value: event.target.value === 'true' })}
                    style={{ flex: 1, minWidth: 110 }}
                  >
                    <option value="true">Verdadeiro</option>
                    <option value="false">Falso</option>
                  </select>
                ) : (
                  <input
                    type={valueType === 'number' ? 'number' : 'text'}
                    aria-label={`Valor da regra ${rowNumber}`}
                    value={rule.value === undefined ? '' : String(rule.value)}
                    disabled={isDisabled}
                    onChange={(event) =>
                      updateRule(index, {
                        value: valueType === 'number' ? convertValue(event.target.value, 'number') : event.target.value,
                      })
                    }
                    style={{ flex: 1, minWidth: 110 }}
                  />
                )}
              </Box>
            )}

            {!isReadOnly && (
              <Box
                component="button"
                type="button"
                className="schema-custom-button"
                aria-label={`Remover regra ${rowNumber}`}
                disabled={isDisabled}
                onClick={() => removeRule(index)}
                sx={{
                  gridArea: 'remove',
                  justifySelf: 'center',
                  width: 30,
                  height: 30,
                  border: 'none',
                  borderRadius: '8px',
                  backgroundColor: 'transparent',
                  color: colors.brand.textMuted,
                  fontSize: '18px',
                  lineHeight: 1,
                  cursor: 'pointer',
                  '&:hover': { color: colors.status.rejected, backgroundColor: `${colors.status.rejected}14` },
                }}
              >
                ×
              </Box>
            )}
          </Box>
        );
      })}

      {rules.length === 0 && (
        <Typography sx={{ fontSize: '12.5px', color: colors.brand.textMuted }}>Nenhuma regra adicionada.</Typography>
      )}

      {fieldHint && rules.some((rule) => !getIdentifier(rule).trim()) && (
        <Typography sx={{ fontSize: '11.5px', color: colors.status.pending }}>{fieldHint}</Typography>
      )}

      {!isReadOnly && (
        <Box
          component="button"
          type="button"
          className="schema-add-item"
          disabled={isDisabled}
          onClick={addRule}
          sx={{
            alignSelf: 'flex-start',
            height: 30,
            paddingX: 1.5,
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '12.5px',
            fontWeight: 500,
            color: colors.brand.textSecondary,
            backgroundColor: 'transparent',
            border: '1px dashed rgba(255, 255, 255, 0.18)',
            '&:hover': { borderColor: colors.primary.yellow, color: colors.primary.yellow },
          }}
        >
          + Adicionar regra
        </Box>
      )}
    </Box>
  );
};
